import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  TextField,
  Typography
} from '@mui/material';
import { Add, Edit, Delete, Close } from '@mui/icons-material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconDefinition, IconName, findIconDefinition, library } from '@fortawesome/fontawesome-svg-core';
import { fas } from '@fortawesome/free-solid-svg-icons';
import { useAppDispatch, useAppSelector } from '../../redux-store/hooks';
import { createFloor, deleteFloor, updateFloor } from '../../services';
import { IClientFloors } from '../../all-types';
import { CommonBtn } from '../../components';

library.add(fas);

const FloorsManagement = () => {
  const floors = useAppSelector(state => state.preloadData.floors);
  const dispatcher = useAppDispatch();

  const [openDialog, setOpenDialog] = useState(false);
  const [currentFloor, setCurrentFloor] = useState<IClientFloors | null>(null);
  const [iconPickerOpen, setIconPickerOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [floorToDelete, setFloorToDelete] = useState<IClientFloors | null>(null);
  const [isInEditMode, setIsInEditMode] = useState(false);
  const [bgImagePickerOpen, setBgImagePickerOpen] = useState(false);

  const [availableIcons, setAvailableIcons] = useState<IconName[]>([]);
  const [availableBgImages, setAvailableBgImages] = useState<string[]>([]);

  useEffect(() => {
    setAvailableIcons([
      'building', 'home', 'hotel', 'stairs', 'elevator',
      'door-open', 'warehouse', 'store', 'hospital', 'school'
    ]);

    setAvailableBgImages([
      'https://firebasestorage.googleapis.com/v0/b/lightworks-tech.appspot.com/o/ruchi-vedika%2Ffloors%2Fbg-1.jpg?alt=media&token=556eecaf-bcca-4e4b-be2e-58d09de407a3',
      'https://firebasestorage.googleapis.com/v0/b/lightworks-tech.appspot.com/o/ruchi-vedika%2Ffloors%2Fbg-2.jpg?alt=media&token=1f3c4a82-d501-4a77-b38c-6dbf9156517e',
      'https://firebasestorage.googleapis.com/v0/b/lightworks-tech.appspot.com/o/ruchi-vedika%2Ffloors%2Fbg-3.jpg?alt=media&token=c664f5dc-7e89-4af2-888e-c842c0a9ba6f',
      'https://firebasestorage.googleapis.com/v0/b/lightworks-tech.appspot.com/o/ruchi-vedika%2Ffloors%2Ftile-brown.png?alt=media&token=cc02ebff-1b76-475b-826f-aee9900df1df',
      'https://firebasestorage.googleapis.com/v0/b/lightworks-tech.appspot.com/o/ruchi-vedika%2Ffloors%2Fbg-4.jpg?alt=media&token=0549e4ce-aefa-44d9-a3a8-dff55c62cc49',
      'https://firebasestorage.googleapis.com/v0/b/lightworks-tech.appspot.com/o/ruchi-vedika%2Ffloors%2Ftiles-bg.jpeg?alt=media&token=7c486553-f8ad-4b72-adc1-86fe8f3e93b5',
      'https://firebasestorage.googleapis.com/v0/b/lightworks-tech.appspot.com/o/ruchi-vedika%2Ffloors%2FChatGPT%20Image%20Jul%2028%2C%202025%2C%2001_39_25%20AM.png?alt=media&token=085fdb5b-46cd-47a3-af10-7a68de7b2d2d',
      'https://firebasestorage.googleapis.com/v0/b/lightworks-tech.appspot.com/o/ruchi-vedika%2Ffloors%2FChatGPT%20Image%20Jul%2028%2C%202025%2C%2001_44_13%20AM.png?alt=media&token=99d1ab45-6231-4e12-bf2c-0e32d37fc30e'
      // Add more URLs here if needed
    ]);
  }, []);

  const getIconDefinition = (iconName: IconName): IconDefinition | null =>
    findIconDefinition({ prefix: 'fas', iconName });

  const handleOpenCreate = () => {
    setCurrentFloor({
      floorNo: 0,
      floorIcon: 'building',
      floorMap: {
        bgImage: '',
        width: 0,
        height: 0,
        floorProps: []
      }
    });
    setOpenDialog(true);
    setIsInEditMode(false);
  };

  const handleOpenEdit = (floor: IClientFloors) => {
    setCurrentFloor(floor);
    setOpenDialog(true);
    setIsInEditMode(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
    setCurrentFloor(null);
    setIsInEditMode(false);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCurrentFloor(prev => ({
      ...prev!,
      [name]: name === 'floorNo' ? parseInt(value) || 0 : value
    }));
  };

  const handleSaveFloor = async () => {
    if (!currentFloor) return;
    try {
      if (isInEditMode && currentFloor._id) {
        await dispatcher(updateFloor({ floor: currentFloor, id: currentFloor._id }));
      } else {
        await dispatcher(createFloor({ floor: currentFloor }));
      }
    } catch (e) {
      console.error(e);
    } finally {
      handleCloseDialog();
    }
  };

  const handleDeleteFloor = async (floor: IClientFloors) => {
    try {
      if (floor._id) {
        await dispatcher(deleteFloor(floor._id));
      }
    } catch (e) {
      console.error(e);
    } finally {
      setDeleteConfirmOpen(false);
    }
  };

  const selectIcon = (icon: IconName) => {
    setCurrentFloor(prev => ({
      ...prev!,
      floorIcon: icon
    }));
    setIconPickerOpen(false);
  };

  const selectBgImage = (url: string) => {
    setCurrentFloor(prev => ({
      ...prev!,
      floorMap: {
        ...prev?.floorMap,
        bgImage: url,
        width: prev?.floorMap?.width || 0,
        height: prev?.floorMap?.height || 0,
        floorProps: prev?.floorMap?.floorProps || []
      }
    }));
    setBgImagePickerOpen(false);
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" sx={{fontWeight: 600}}>Floors Management</Typography>
        <CommonBtn variant="contained" color="primary" startIcon={<Add />} onClick={handleOpenCreate}>
          Add New Floor
        </CommonBtn>
      </Box>

      <Box sx={{ display: 'grid', gap: 2, gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))' }}>
        {floors?.map((floor) => {
          const iconDef = getIconDefinition(floor.floorIcon as IconName || 'building');
          return (
            <Paper
              key={floor.floorNo}
              elevation={3}
              sx={{ p: 2, display: 'flex', flexDirection: 'column', alignItems: 'center', position: 'relative', borderRadius: 5 }}
            >
              <FontAwesomeIcon icon={iconDef!} size="3x" />
              <Typography variant="h6" mt={1}>Floor {floor.floorNo}</Typography>
              <Box mt={2}>
                <IconButton onClick={() => handleOpenEdit(floor)}>
                  <Edit color="primary" />
                </IconButton>
                <IconButton onClick={() => {
                  setFloorToDelete(floor);
                  setDeleteConfirmOpen(true);
                }}>
                  <Delete color="error" />
                </IconButton>
              </Box>
            </Paper>
          );
        })}
      </Box>

      {/* Create/Edit Dialog */}
      <Dialog open={openDialog} onClose={handleCloseDialog} fullWidth maxWidth="sm">
        <DialogTitle>
          {isInEditMode ? 'Edit Floor' : 'Add New Floor'}
          <IconButton aria-label="close" onClick={handleCloseDialog} sx={{ position: 'absolute', right: 8, top: 8 }}>
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <TextField
            fullWidth
            label="Floor Number"
            name="floorNo"
            type="number"
            value={currentFloor?.floorNo || ''}
            onChange={handleInputChange}
            margin="normal"
          />
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>Floor Icon</Typography>
            <Button
              variant="outlined"
              onClick={() => setIconPickerOpen(true)}
              startIcon={
                currentFloor?.floorIcon
                  ? <FontAwesomeIcon icon={getIconDefinition(currentFloor.floorIcon as IconName)!} />
                  : null
              }
            >
              {currentFloor?.floorIcon || 'Select Icon'}
            </Button>
          </Box>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle1" gutterBottom>Background Image</Typography>
            <Button
              variant="outlined"
              onClick={() => setBgImagePickerOpen(true)}
            >
              {currentFloor?.floorMap?.bgImage
                ? 'Change Background'
                : 'Select Background'}
            </Button>
          </Box>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Width"
              type="number"
              value={currentFloor?.floorMap?.width || ''}
              onChange={(e) =>
                setCurrentFloor(prev => ({
                  ...prev!,
                  floorMap: {
                    ...prev?.floorMap,
                    width: parseInt(e.target.value) || 0,
                    height: prev?.floorMap?.height || 0,
                    floorProps: prev?.floorMap?.floorProps || []
                  }
                }))
              }
              margin="normal"
            />
            <TextField
              fullWidth
              label="Height"
              type="number"
              value={currentFloor?.floorMap?.height || ''}
              onChange={(e) =>
                setCurrentFloor(prev => ({
                  ...prev!,
                  floorMap: {
                    ...prev?.floorMap,
                    height: parseInt(e.target.value) || 0,
                    width: prev?.floorMap?.width || 0,
                    floorProps: prev?.floorMap?.floorProps || []
                  }
                }))
              }
              margin="normal"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button onClick={handleSaveFloor} variant="contained" color="primary">Save</Button>
        </DialogActions>
      </Dialog>

      {/* Icon Picker Dialog */}
      <Dialog open={iconPickerOpen} onClose={() => setIconPickerOpen(false)} maxWidth="md">
        <DialogTitle>Select Floor Icon</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 2 }}>
            {availableIcons.map((icon) => {
              const iconDef = getIconDefinition(icon);
              return (
                <IconButton
                  key={icon}
                  onClick={() => selectIcon(icon)}
                  sx={{
                    border: currentFloor?.floorIcon === icon ? '2px solid' : '1px solid',
                    borderColor: currentFloor?.floorIcon === icon ? 'primary.main' : 'divider',
                    p: 3
                  }}
                >
                  {iconDef && <FontAwesomeIcon icon={iconDef} size="2x" />}
                </IconButton>
              );
            })}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIconPickerOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Background Image Picker Dialog */}
      <Dialog open={bgImagePickerOpen} onClose={() => setBgImagePickerOpen(false)} maxWidth="md">
        <DialogTitle>Select Background Image</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
            {availableBgImages.map((img) => (
              <Box
                key={img}
                sx={{
                  border: currentFloor?.floorMap?.bgImage === img ? '2px solid' : '1px solid',
                  borderColor: currentFloor?.floorMap?.bgImage === img ? 'primary.main' : 'divider',
                  p: 1,
                  cursor: 'pointer',
                  textAlign: 'center'
                }}
                onClick={() => selectBgImage(img)}
              >
                <img src={img} alt="bg" style={{ width: '100%', height: 100, objectFit: 'cover' }} />
                <Typography variant="caption">{img.split('/').pop()?.split('?')[0]}</Typography>
              </Box>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBgImagePickerOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={deleteConfirmOpen} onClose={() => setDeleteConfirmOpen(false)}>
        <DialogTitle>Confirm Deletion</DialogTitle>
        <DialogContent>Are you sure you want to delete floor {floorToDelete?.floorNo}?</DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button
            onClick={() => floorToDelete && handleDeleteFloor(floorToDelete)}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export { FloorsManagement as FloorScreen };
